import { Component, OnInit } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { CreditsService } from '../../../shared/services/credits.service';
import { UserService } from '../../../authentication/services/user.service';
import { UserProfile } from '../../../authentication/profile/models/user-profile';
import {
    CreditsOverviewDto,
    IncreaseCreditsRequestDto,
    DecreaseCreditsRequestDto,
    ChangeSubscriptionRequestDto,
    CreditType,
    SubscriptionChangeType,
    SubscriptionType,
} from '../../../shared/models/credit-service-dtos';
import { MessageService } from 'src/app/message/services/message.service';
import { TranslateService } from '@ngx-translate/core';
import { AlgorithmValidator } from 'src/app/algorithm/validators/algorithm-validator';

@Component({
    selector: 'app-admin-credits',
    templateUrl: './admin-credits.component.html',
    styleUrls: ['./admin-credits.component.scss'],
    standalone: false,
})
export class AdminCreditsComponent implements OnInit {
    // Form groups
    increaseCreditsForm: FormGroup;
    decreaseCreditsForm: FormGroup;
    changeSubscriptionForm: FormGroup;

    // Data properties
    usersData: CreditsOverviewDto = undefined;
    selectedUserId: string = '';
    selectedUser: UserProfile | null = null;
    users: {
        id: string;
        firstName: string;
        lastName: string;
        displayName: string;
    }[] = [];
    isLoading: boolean = false;

    // Enums for template
    CreditType = CreditType;
    SubscriptionChangeType = SubscriptionChangeType;
    SubscriptionType = SubscriptionType;

    constructor(
        private fb: FormBuilder,
        private creditsService: CreditsService,
        private userService: UserService,
        private messageService: MessageService,
        private translateService: TranslateService
    ) {
        this.initializeForms();
    }

    ngOnInit(): void {
        this.loadUsers();
    }

    private loadUsers(): void {
        this.isLoading = true;
        this.userService.getUsers().subscribe({
            next: (users) => {
                this.users = users.sort((u1, u2) =>
                    u1.displayName.localeCompare(u2.displayName)
                );
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error loading users:', error);
                this.showMessage(
                    this.translateService.instant(
                        'admin.components.credits.userList.loadingError'
                    ),
                    false
                );
            },
        });
    }

    private initializeForms(): void {
        // Add Credits Form
        this.increaseCreditsForm = this.fb.group({
            amount: [
                '0',
                [
                    Validators.required,
                    AdminCreditsComponent.validateCreditAmount,
                ],
            ],
            creditType: [CreditType.INCLUSIVE, [Validators.required]],
            cause: ['', [Validators.maxLength(188)]],
        });

        // Decrease Credits Form
        this.decreaseCreditsForm = this.fb.group({
            amount: [
                '0',
                [
                    Validators.required,
                    AdminCreditsComponent.validateCreditAmount,
                ],
            ],
            cause: ['', [Validators.maxLength(188)]],
            isSpend: [true, [Validators.required]],
        });

        // Change Subscription Form
        this.changeSubscriptionForm = this.fb.group({
            subscriptionType: [SubscriptionType.FREE, [Validators.required]],
            changeType: [
                SubscriptionChangeType.SUBSCRIPTION_END,
                [Validators.required],
            ],
            prepaidPeriods: [
                null,
                [AlgorithmValidator.validatePositiveInteger],
            ],
            isAutoRenew: [false],
        });
    }

    onUserSelect(user: UserProfile): void {
        this.selectedUser = user;
        this.selectedUserId = user.id;
        this.loadUserCreditsData(user.id);
    }

    private loadUserCreditsData(userId: string): void {
        this.isLoading = true;

        // Load both overview and budget data
        this.creditsService
            .getOverviewForUser(userId)
            .pipe(
                tap((data) => {
                    this.usersData = data;
                    this.isLoading = false;
                }),
                catchError((error) => {
                    this.usersData = undefined;
                    console.error('Error loading credits overview:', error);
                    let cause = '';
                    if (
                        error.error.message.includes('Subscription') &&
                        error.error.message.includes('not found')
                    ) {
                        cause = this.translateService.instant(
                            'admin.components.credits.userOverview.subscriptionNotFound'
                        );
                    }
                    this.showMessage(
                        this.translateService.instant(
                            'admin.components.credits.userOverview.loadingError'
                        ) + cause,
                        false
                    );

                    return of(null);
                })
            )
            .subscribe();
    }

    onIncreaseCredits(): void {
        if (this.increaseCreditsForm.valid && this.selectedUserId) {
            this.isLoading = true;
            const formValue = this.increaseCreditsForm.value;

            const request: IncreaseCreditsRequestDto = {
                amount: Number(
                    AdminCreditsComponent.controlValueParser(formValue.amount)
                ),
                trybUserId: this.selectedUserId,
                creditType: formValue.creditType,
                creditExpirationDate:
                    this.usersData.currentSubscription.periodEndDate,
                cause: formValue.cause,
            };

            this.creditsService.increaseCredits(request).subscribe({
                next: () => {
                    this.showMessage(
                        this.translateService.instant(
                            'admin.components.credits.increase.success'
                        ),
                        true
                    );
                    this.loadUserCreditsData(this.selectedUserId);
                },
                error: (error) => {
                    console.error('Error increasing credits:', error);
                    this.showMessage(
                        this.translateService.instant(
                            'admin.components.credits.increase.failed'
                        ),
                        false
                    );
                    this.isLoading = false;
                },
            });
        }
    }

    onDecreaseCredits(): void {
        if (this.decreaseCreditsForm.valid && this.selectedUserId) {
            this.isLoading = true;
            const formValue = this.decreaseCreditsForm.value;

            const request: DecreaseCreditsRequestDto = {
                amount: Number(
                    AdminCreditsComponent.controlValueParser(formValue.amount)
                ),
                trybUserId: this.selectedUserId,
                cause: formValue.cause,
                isSpend: formValue.isSpend,
            };

            this.creditsService.decreaseCredits(request).subscribe({
                next: () => {
                    this.showMessage(
                        this.translateService.instant(
                            'admin.components.credits.decrease.success'
                        ),
                        true
                    );
                    this.loadUserCreditsData(this.selectedUserId);
                },
                error: (error) => {
                    console.error('Error decreasing credits:', error);
                    this.showMessage(
                        this.translateService.instant(
                            'admin.components.credits.decrease.failed'
                        ),
                        false
                    );
                },
            });
        }
    }

    onChangeSubscription(): void {
        if (this.changeSubscriptionForm.valid && this.selectedUserId) {
            this.isLoading = true;
            const formValue = this.changeSubscriptionForm.value;

            const request: ChangeSubscriptionRequestDto = {
                subscriptionType: formValue.subscriptionType,
                changeType: formValue.changeType,
                prepaidPeriods: formValue.prepaidPeriods,
                trybUserId: this.selectedUserId,
                isAutoRenew: formValue.isAutoRenew,
            };

            this.creditsService.changeSubscription(request).subscribe({
                next: () => {
                    this.showMessage(
                        this.translateService.instant(
                            'admin.components.credits.changeSubscription.success'
                        ),
                        true
                    );
                    this.loadUserCreditsData(this.selectedUserId);
                },
                error: (error) => {
                    console.error('Error changing subscription:', error);
                    this.showMessage(
                        this.translateService.instant(
                            'admin.components.credits.changeSubscription.failed'
                        ),
                        false
                    );
                },
            });
        }
    }

    onRefreshUserData(): void {
        if (this.selectedUserId) {
            this.loadUserCreditsData(this.selectedUserId);
        }
    }

    private showMessage(message: string, isSuccess: boolean): void {
        if (isSuccess) {
            this.messageService.displaySuccessMessage(message);
        } else {
            this.messageService.displayErrorMessage(message);
        }
        this.isLoading = false;
    }

    static validateCreditAmount(
        control: AbstractControl
    ): ValidationErrors | null {
        const value: string = AdminCreditsComponent.controlValueParser(
            control.value
        );

        const formatError = {
            creditAmountNotInRange: {
                inputValue: control.value,
            },
        };

        if (value === '') {
            return formatError;
        }

        const number: number = Number(value);
        if (number <= 0 || number >= 100000 || Number.isNaN(number)) {
            return formatError;
        }
        return null;
    }

    private static controlValueParser(value: any): string {
        return !value ? '' : value.toString().replace(',', '.');
    }
}
